import { Injectable } from '@angular/core';
import {
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Route,
  Router,
  RoutesRecognized
} from '@angular/router';
import { format } from 'date-fns';
import {
  cloneDeep as _cloneDeep,
  forOwn as _forOwn,
  isArray as _isArray,
  isEmpty as _isEmpty,
  isNaN as _isNaN,
  isNull as _isNull,
  isObject as _isObject,
  isString as _isString,
  isUndefined as _isUndefined,
  pull as _pull
} from 'lodash';

// date-fns
import { PrettyPolicyNum } from '@amfam/policy/pipes';

import { WindowRef } from '../window/window.service';

@Injectable({
  providedIn: 'root'
})
export class UtilService {
  constructor(private win: WindowRef) {}

  getBaseUrl(): string {
    return (
      location.protocol +
      '//' +
      location.hostname +
      (location.port ? ':' + location.port : '') +
      '/'
    );
  }

  getAbsoluteUrl(relativePath: string, experienceId: string): string {
    if (relativePath.startsWith('/')) {
      relativePath = relativePath.slice(1);
    }

    if (experienceId && !relativePath.startsWith(experienceId)) {
      relativePath = experienceId + '/' + relativePath;
    }

    return this.getBaseUrl() + relativePath;
  }
  scrollToElementById(elementId: string) {
    const element = document.getElementById(elementId);
    if (element) {
      this.win.nativeWindow.scrollTo(0, element.offsetTop);
    }
  }

  scrollToTop() {
    this.win.nativeWindow.scrollTo(0, 0);
  }

  pruneEmpty(obj) {
    return (function prune(current) {
      _forOwn(current, function (value, key) {
        if (
          _isUndefined(value) ||
          _isNull(value) ||
          _isNaN(value) ||
          (_isString(value) && _isEmpty(value)) ||
          (_isObject(value) && _isEmpty(prune(value)))
        ) {
          delete current[key];
        }
      });
      // remove any leftover undefined values from the delete
      // operation on an array
      if (_isArray(current)) {
        _pull(current, undefined);
      }
      return current;
    })(_cloneDeep(obj)); // Do not modify the original object, create a clone instead
  }

  // TODO this library should be full of static pure functions, not injected as a service
  /*
    Used to generate a correlation Id for ngrx/store Request-Reply pattern.
    Request Reply Pattern Info: https://blog.nrwl.io/ngrx-patterns-and-techniques-f46126e2b1e5
    Synthetic GUID generator: http://guid.us/GUID/JavaScript
    TODO: @angular uses uuid, we could use that as well with no increase in bundle size.
    see: https://stackoverflow.com/questions/45754907/how-to-generate-uuid-with-angular-2
  */
  /* eslint-disable no-bitwise */
  generateId() {
    function S4() {
      return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
    }
    const guid = (
      S4() +
      S4() +
      '-' +
      S4() +
      S4() +
      '-' +
      S4() +
      '-' +
      S4() +
      S4() +
      S4()
    ).toLowerCase();
    return guid;
  }

  /**
   * @author: Abhishek Singh
   * @description: This function would take in a string, and return the extracted policy number from the string.
   */
  extractPolicyNumber(policyNum: string): string {
    const policyLength = policyNum.length;

    if (policyLength < 10) {
      // Return first 8 characters
      return policyNum.substr(0, 8);
    } else if (policyLength === 10) {
      // Return unmodified string
      return policyNum;
    } else if (policyLength < 12) {
      // Return first 10 characters
      return policyNum.substr(0, 10);
    } else if (policyLength >= 12) {
      if (policyNum.startsWith('41')) {
        // If length is greater than 12 and starts with '41', return first 12 digits
        return policyNum.substr(0, 12);
      } else {
        // If length is greater than 12 and doesn't start with '41', return first 10 digits
        return policyNum.substr(0, 10);
      }
    } else {
      return '';
    }
  }
  /*
  public returnQuoteDollarOrCents(
    totalCost: number,
    termMonths: number,
    returnType: 'Dollar' | 'Cent'
  ): string {
    if (returnType === 'Dollar') {
      return String(_round(_divide(Number(totalCost.toFixed(2)), termMonths), 2)).split('.')[0];
    } else {
      return String(_divide(totalCost, termMonths).toFixed(2)).split('.')[1];
    }
  }*/

  public returnQuoteDollarOrCents(amount: number, returnType: 'Dollar' | 'Cent'): string {
    if (returnType === 'Dollar') {
      return String(amount.toFixed(2)).split('.')[0];
    } else {
      return String(amount.toFixed(2)).split('.')[1];
    }
  }

  maskPolicyNumber(policyNumber: string) {
    const partialPolicyNumber = new PrettyPolicyNum().transform(policyNumber).substring(5);
    return `xxxxx${partialPolicyNumber}`;
  }

  isIE11AndBelow() {
    return !!this.win.nativeWindow.navigator.userAgent.match(/MSIE|Trident/);
  }

  onlyAlphanumeric(input: string): string {
    if (!input) return '';
    return input.replace(/[^a-zA-Z0-9]/g, '');
  }

  printAllRoutes(router: Router) {
    router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        console.log('🟡 NavigationStart:', event.url);
      }

      if (event instanceof RoutesRecognized) {
        console.log('🟢 RoutesRecognized:', event.url, event.state);
      }

      if (event instanceof NavigationEnd) {
        console.log('✅ NavigationEnd:', event.url);
        this.logAllRoutes(router);
      }

      if (event instanceof NavigationError) {
        console.error('🔴 NavigationError:', event.error);
      }
    });

    // Log routes initially (before lazy routes load)
    this.logAllRoutes(router);
  }

  logAllRoutes(router: Router) {
    console.log('🔍 All Route Configurations:');
    this.printRoutes(router.config);
  }

  printRoutes(routes: Route[], parentPath = '') {
    for (const route of routes) {
      const path = route.path ? `${parentPath}/${route.path}` : parentPath || '/';
      console.log(`📍 ${path}`, route);

      if (route.children) {
        this.printRoutes(route.children, path);
      }

      // Lazy-loaded routes are stored in _loadedConfig once loaded
      if ((route as any)._loadedConfig) {
        const loadedRoutes = (route as any)._loadedConfig.routes;
        this.printRoutes(loadedRoutes, path);
      }
    }
  }
}
